import { useId, useRef, useState } from 'react';
import { compressImageForUpload } from '../../utils/imageCompression.js';
import { api } from '../../api/client.js';

/** `value` is a media object `{id, url}` or null. `onChange(media | null)`. */
export function ImageUploadField({ label, value, onChange, kind = 'MENU_ITEM_IMAGE', hint }) {
  const [status, setStatus] = useState('idle'); // idle | processing | uploading | error
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const inputId = useId();
  const busy = status === 'processing' || status === 'uploading';
  const assetName = kind === 'LOGO'
    ? 'logo'
    : kind === 'BANNER'
      ? 'cover image'
      : kind === 'CATEGORY_IMAGE'
        ? 'section cover image'
        : kind === 'OTHER'
          ? 'image'
          : 'dish photo';
  const assetArticle = assetName === 'image' ? 'an image' : `a ${assetName}`;

  async function handleFileChange(event) {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-selecting the same file again later
    if (!file) return;

    setError(null);
    setStatus('processing');
    try {
      const blob = await compressImageForUpload(file);
      setStatus('uploading');
      const formData = new FormData();
      formData.append('file', blob, `upload.${blob.type === 'image/webp' ? 'webp' : 'jpg'}`);
      formData.append('kind', kind);
      const media = await api.upload('/api/media', formData);
      onChange(media);
      setStatus('idle');
    } catch (err) {
      setError(err.message || 'Could not upload that image.');
      setStatus('error');
    }
  }

  return (
    <div className="field image-upload-field">
      {label && <label htmlFor={inputId}>{label}</label>}
      <div className={`image-upload-field__dropzone${value?.url ? ' has-image' : ''}`}>
        {value?.url ? (
          <img src={value.url} alt={`Selected ${assetName}`} onError={(event) => { event.currentTarget.hidden = true; }} />
        ) : (
          <div className="image-upload-field__placeholder" aria-hidden="true">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="2" /><path d="m21 15-4-4L6 20" /></svg>
          </div>
        )}
        <div className="image-upload-field__actions">
          <strong>{value ? `${assetName[0].toUpperCase()}${assetName.slice(1)} added` : `Add ${assetArticle}`}</strong>
          <span>JPG, PNG, WebP, GIF, AVIF, or TIFF</span>
          <div>
            <button type="button" className="btn btn-sm" onClick={() => inputRef.current?.click()} disabled={busy}>
              {status === 'processing' && <span className="spinner" aria-hidden="true" />}
              {status === 'processing' ? 'Preparing…' : status === 'uploading' ? 'Uploading…' : value ? 'Replace photo' : 'Choose photo'}
            </button>
            {value && !busy && <button type="button" className="btn btn-sm btn-ghost" onClick={() => onChange(null)}>Remove</button>}
          </div>
        </div>
      </div>
      <input id={inputId} ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/avif,image/tiff" hidden onChange={handleFileChange} />
      {hint && <div className="field-hint">{hint}</div>}
      {error && <div className="field-error" role="alert">{error}</div>}
    </div>
  );
}
